import type React from 'react'
import { useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'

import { useToast } from '@chakra-ui/react'
import {
  useAddProductAnswersMutation,
  useGetComplianceOnboardingStateQuery,
} from '../redux/services/coreApi'
import type { RootState } from '../redux/store'
import type { Q4BBrokerUse } from '../types/coreApi-types'
import { Q4B_OPTIONS } from '../utils/complianceQuestionnaireOptions'
import { isAdmin, isPartner, isWealthManager } from '../utils/roleUtils'
import Toast from './Toast'

interface BrokerActivationGateProps {
  children: React.ReactNode
}

/**
 * Product-activation gate for the Broker product (ADR-0005). Reads the same
 * derived predicate the API's swap-creation gate enforces (the onboarding-state
 * query's per-product activation), so what this shows can never disagree with
 * what the API blocks. A customer who onboarded without declaring Broker —
 * including a migrated Veli customer, who holds only Penning Index by default —
 * answers the Broker product questions right here and is re-scored before the
 * screen unlocks. Activation is orthogonal to restrictions: the re-score may
 * still land the customer in a review that blocks trading, which the existing
 * compliance banners surface.
 */
const BrokerActivationGate: React.FC<BrokerActivationGateProps> = ({
  children,
}) => {
  const history = useHistory()
  const toast = useToast()
  const sessionUser = useSelector((state: RootState) => state.session.user)
  const [selected, setSelected] = useState<Q4BBrokerUse[]>([])

  // The gate applies to investor-side users only; staff roles never hold the
  // onboarding state (mirrors AppNavigator's skip).
  const isStaff =
    isAdmin(sessionUser.role) ||
    isPartner(sessionUser.role) ||
    isWealthManager(sessionUser.role)

  const { data: onboardingState, isLoading } =
    useGetComplianceOnboardingStateQuery(undefined, { skip: isStaff })
  const [addProductAnswers, { isLoading: isSubmitting }] =
    useAddProductAnswersMutation()

  const broker = onboardingState?.products?.find(p => p.product === 'Broker')

  // Pass through while loading (no gate flash — the API enforces regardless),
  // for staff, when no product activation is reported, and of course once
  // Broker is activated. A company (TEMP legacy flow, no questionnaire) has
  // Broker activated as soon as its base onboarding is done.
  if (isStaff || isLoading || !broker || broker.activated) {
    return <>{children}</>
  }

  const needsBaseOnboarding = broker.missing.includes('BaseOnboarding')

  const toggle = (value: Q4BBrokerUse) =>
    setSelected(current =>
      current.includes(value)
        ? current.filter(v => v !== value)
        : [...current, value]
    )

  const handleActivate = async () => {
    try {
      const state = await addProductAnswers({
        product: 'Broker',
        q4Broker: selected,
        q4Invest: [],
      }).unwrap()

      const nowActivated = state.products.find(
        p => p.product === 'Broker'
      )?.activated
      toast({
        position: 'top-right',
        render: () => (
          <Toast type="success">
            {nowActivated
              ? 'Broker service activated. Your risk assessment has been updated.'
              : 'Your answers were submitted.'}
          </Toast>
        ),
      })
      // The mutation invalidates the onboarding state, so the gate unlocks (or
      // the standard compliance banners take over if the re-score opened a
      // review) without any state juggling here.
    } catch {
      toast({
        position: 'top-right',
        render: () => (
          <Toast type="error">Something went wrong. Please try again.</Toast>
        ),
      })
    }
  }

  return (
    <div className="relative">
      <div className="pointer-events-none select-none blur-sm opacity-40">
        {children}
      </div>
      <div className="absolute inset-0 flex items-start justify-center pt-10">
        <div className="card p-8 max-w-lg w-full shadow-xl">
          {needsBaseOnboarding ? (
            <div className="text-center">
              <h3 className="text-lg font-semibold mb-2">
                Complete onboarding first
              </h3>
              <p className="text-description mb-6">
                Finish your onboarding steps before using the broker service.
              </p>
              <button
                className="button w-full"
                onClick={() => history.push('/app/get-started')}
              >
                Go to Get started
              </button>
            </div>
          ) : (
            <>
              <h3 className="text-lg font-semibold mb-2">
                Activate the broker service
              </h3>
              <p className="text-description mb-4">
                You haven&apos;t activated the broker service yet. Tell us how
                you plan to use it — your risk assessment is updated with your
                answers before you can trade.
              </p>
              <div className="space-y-2 mb-6 text-left">
                {Q4B_OPTIONS.map(option => (
                  <label
                    key={option.value}
                    className="flex items-start gap-3 cursor-pointer p-3 rounded-md border border-black/5 dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/5"
                  >
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={selected.includes(option.value)}
                      onChange={() => toggle(option.value)}
                    />
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>
              <button
                className="button w-full"
                disabled={selected.length === 0 || isSubmitting}
                onClick={handleActivate}
              >
                {isSubmitting ? 'Activating…' : 'Activate broker service'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default BrokerActivationGate
